import React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

interface Props {
    currentPage: number;
    totalItems: number;
    pageSize: number;
    pageChanged: Function;
    showInfo?: boolean;
    maxVisiblePages?: number;
}

const Pagination: React.FC<Props> = ({
    currentPage = 1,
    totalItems = 0,
    pageSize = 0,
    pageChanged,
    showInfo = true,
    maxVisiblePages = 7,
}: Props) => {
    const totalPages = Math.ceil(totalItems / pageSize);

    if (totalPages <= 1) return null;

    const getVisiblePages = () => {
        if (totalPages <= maxVisiblePages) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const delta = Math.floor(maxVisiblePages / 2);
        const range = [];
        const rangeWithDots = [];

        const start = Math.max(2, currentPage - delta);
        const end = Math.min(totalPages - 1, currentPage + delta);

        if (start - 1 === 2) {
            range.push(1, 2);
        } else if (start - 1 !== 1) {
            range.push(1, '...');
        } else {
            range.push(1);
        }

        for (let i = start; i <= end; i++) {
            range.push(i);
        }

        if (end + 1 === totalPages - 1) {
            range.push(totalPages - 1, totalPages);
        } else if (end + 1 !== totalPages) {
            range.push('...', totalPages);
        } else {
            range.push(totalPages);
        }

        return range;
    };

    const visiblePages = getVisiblePages();
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {showInfo && (
                <div className="text-sm text-gray-700 order-2 sm:order-1">
                    Showing <span className="font-medium">{startItem}</span> to{' '}
                    <span className="font-medium">{endItem}</span> of <span className="font-medium">{totalItems}</span>{' '}
                    results
                </div>
            )}

            <nav className="flex items-center space-x-1 order-1 sm:order-2" aria-label="Pagination">
                {/* Previous Button */}
                <button
                    onClick={() => pageChanged(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className={`
                        inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-medium transition-colors
                        ${
                            currentPage <= 1
                                ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                                : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50 bg-white border border-gray-300'
                        }
                    `}
                    aria-label="Go to previous page"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Page Numbers */}
                {visiblePages.map((page, index) => {
                    if (page === '...') {
                        return (
                            <span
                                key={`ellipsis-${index}`}
                                className="inline-flex items-center justify-center w-8 h-8 text-gray-400"
                            >
                                <MoreHorizontal className="w-4 h-4" />
                            </span>
                        );
                    }

                    const isActive = page === currentPage;
                    return (
                        <button
                            key={page}
                            onClick={() => pageChanged(page)}
                            className={`
                                inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-medium transition-colors
                                ${
                                    isActive
                                        ? 'text-white bg-blue-600 hover:bg-blue-700'
                                        : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50 bg-white border border-gray-300'
                                }
                            `}
                            aria-label={`Go to page ${page}`}
                            aria-current={isActive ? 'page' : undefined}
                        >
                            {page}
                        </button>
                    );
                })}

                {/* Next Button */}
                <button
                    onClick={() => pageChanged(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className={`
                        inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-medium transition-colors
                        ${
                            currentPage >= totalPages
                                ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                                : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50 bg-white border border-gray-300'
                        }
                    `}
                    aria-label="Go to next page"
                >
                    {' '}
                    <ChevronRight className="w-4 h-4" />
                </button>
            </nav>
        </div>
    );
};

export default Pagination;
